import { injectAesthetic } from './shared-styles.ts';
import './app-button.ts';

export class AppSaveBar extends HTMLElement {
  private _active = false;
  private _isLoading = false;
  private _seed = Math.floor(Math.random() * 10000);
  private _initialValues = new Map<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, string>();
  private _observer: MutationObserver | null = null;

  static get observedAttributes() {
    return ['active', 'loading', 'message'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.render();
    this.setupListeners();
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    this.stopObservingInputs();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return;
    if (name === 'active') this._active = newValue !== null;
    if (name === 'loading') this._isLoading = newValue !== null;
    this.render();
  }

  set active(val: boolean) {
    if (val) this.setAttribute('active', '');
    else this.removeAttribute('active');
  }

  get active() {
    return this._active;
  }

  set loading(val: boolean) {
    if (val) this.setAttribute('loading', '');
    else this.removeAttribute('loading');
  }

  private setupListeners() {
    this.shadowRoot?.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.id === 'save-btn' || target.closest('#save-btn')) {
        this.dispatchEvent(new CustomEvent('save', { bubbles: true, composed: true }));
      } else if (target.id === 'cancel-btn' || target.closest('#cancel-btn')) {
        this.dispatchEvent(new CustomEvent('cancel', { bubbles: true, composed: true }));
      }
    });
  }

  public resetInitialState(container: HTMLElement) {
    this._initialValues.clear();
    const inputs = Array.from(container.querySelectorAll('input, textarea, select')) as (HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement)[];
    inputs.forEach(input => {
      if (input.closest('app-modal')) return;
      const val = input.type === 'checkbox' ? (input as HTMLInputElement).checked.toString() : input.value;
      this._initialValues.set(input, val);
    });
    this.active = false;
  }

  public checkDirty(container: HTMLElement) {
    const inputs = Array.from(container.querySelectorAll('input, textarea, select')) as (HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement)[];
    let isDirty = false;
    inputs.forEach(input => {
      if (input.closest('app-modal')) return;
      const initial = this._initialValues.get(input);
      const current = input.type === 'checkbox' ? (input as HTMLInputElement).checked.toString() : input.value;
      if (initial !== undefined && current !== initial) {
        isDirty = true;
      }
    });
    this.active = isDirty;
  }

  private stopObservingInputs() {
    // Listeners are removed automatically when element is destroyed if they are bound correctly
  }

  private render() {
    if (!this.shadowRoot) return;

    const message = this.getAttribute('message') || 'Careful — you have unsaved changes!';

    this.shadowRoot.innerHTML = `
      ${injectAesthetic(this._seed)}
      <style>
        :host {
          position: fixed;
          bottom: 0;
          left: 60px; /* Account for control strip */
          right: 0;
          display: flex;
          justify-content: center;
          padding: 20px 40px;
          pointer-events: none;
          z-index: 1000;
          transform: translateY(100%);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        :host([active]) {
          transform: translateY(0);
        }

        .bar-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          width: 100%;
          max-width: 800px;
          padding: 16px 24px;
          background: rgba(10, 10, 10, 0.95);
          backdrop-filter: blur(12px);
          pointer-events: auto;
          position: relative;
        }

        .bar-container::before {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          border: 1px solid white;
          filter: url(#filt-strong-${this._seed});
          pointer-events: none;
          opacity: 0.6;
        }

        .message {
          font-family: var(--font-mono);
          font-size: 0.85rem;
          color: white;
        }

        .actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        @media (max-width: 768px) {
          :host { left: 0; }
          .bar-container { flex-direction: column; text-align: center; gap: 16px; }
        }
      </style>

      <div class="bar-container">
        <div class="message">${message}</div>
        <div class="actions">
          <app-button id="cancel-btn" variant="ghost" size="sm" icon="rotate-ccw" ${this._isLoading ? 'disabled' : ''}></app-button>
          <app-button id="save-btn" variant="primary" size="sm" icon="check" ${this._isLoading ? 'loading' : ''}></app-button>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('app-save-bar')) {
  customElements.define('app-save-bar', AppSaveBar);
}
